import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import {ERROR, UNIT} from '@di-shared/const';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import EnergyPowerRecentGraph from '@di-shared/features/history/energy-usage/EnergyPowerRecentGraph';
import {EnergyUsageRecentProps} from '@di-shared/features/history/energy-usage/EnergyUsageRecent/index';
import {roundToFirstDecimal} from '@di-shared/features/history/energy-usage/utils';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';


export default function ({vin}: EnergyUsageRecentProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: EnergyUsageRecentProps) {
  const ccId = environmentStore.getState().ccid;
  const {data: evRecentData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getRecentEvDriveStats), vin, ccId],
    queryFn: () => diApi.driveHistory.getRecentEvDriveStats({vin, ccId}),
    select: ({data}) => data,
  });

  const isRecentEnergyUsageNone =
    evRecentData.latestDriveDate === '' &&
    evRecentData.powerConsumption === 0 &&
    evRecentData.motorConsumptionRatio === 0 &&
    evRecentData.climateConsumptionRatio === 0 &&
    evRecentData.electricDeviceConsumptionRatio === 0 &&
    evRecentData.batteryManageConsumptionRatio === 0 &&
    evRecentData.avrMileage === 0 &&
    evRecentData.regenPower === 0;

  // No data 화면
  if (isRecentEnergyUsageNone) {
    return <None />;
  }

  // Default 화면
  return <Default vin={vin} />;
}

function None() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  const today = new Date();

  return (
    <section className="section">
      <h2 className="blind">{t('T10002')}</h2>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10184')}
        </h3>
        <span className="section_heading_caption">
          {t('T10077', {date: CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')})}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div className="device_info_item">
            <h4 className="device_info_key body_medium_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {0}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('T10078')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('T10079')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('T10080')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('T10081')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10037')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {0}
              <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </span>
        </div>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10072')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {0}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function Default({vin}: EnergyUsageRecentProps) {
  const ccId = environmentStore.getState().ccid;
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {data: evRecentData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getRecentEvDriveStats), vin, ccId],
    queryFn: () => diApi.driveHistory.getRecentEvDriveStats({vin, ccId}),
    select: ({data}) => data,
  });
  const {convertEnergyEfficiencySymbol, convertEnergyEfficiencyValue} = useUnitConverter();

  const verifiedRecentDrivingDate = parseDateWithFormat(evRecentData.latestDriveDate);
  if (!verifiedRecentDrivingDate) {
    throw new Error('latestDriveDate error');
  }

  return (
    <section className="section">
      <h2 className="blind">{t('T10002')}</h2>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10070')}
        </h3>
        <span className="section_heading_caption">
          {t('T10077', {date: CldrDatetimeFormatter.format(verifiedRecentDrivingDate, 'yMMMd')})}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div className="device_info_item">
            <h4 className="device_info_key body_medium_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {formatNumberWithCommas(roundToFirstDecimal(evRecentData.powerConsumption))}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={evRecentData.motorConsumptionRatio}
            electricDeviceRatio={evRecentData.electricDeviceConsumptionRatio}
            climateRatio={evRecentData.climateConsumptionRatio}
            batteryManageRatio={evRecentData.batteryManageConsumptionRatio}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('T10078')}</span>
                <span className="legend_value">{evRecentData.motorConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('T10079')}</span>
                <span className="legend_value">{evRecentData.electricDeviceConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('T10080')}</span>
                <span className="legend_value">{evRecentData.climateConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('T10081')}</span>
                <span className="legend_value">{evRecentData.batteryManageConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10037')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {formatNumberWithCommas(convertEnergyEfficiencyValue(evRecentData.avrMileage, {skipFormatting: true}))}
              <span className="body_small_r">{convertEnergyEfficiencySymbol(evRecentData.avrMileage, {skipFormatting: true})}</span>
            </span>
          </span>
        </div>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10072')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {formatNumberWithCommas(roundToFirstDecimal(evRecentData.regenPower))}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();

  return (
    <section className="section">
      <h2 className="blind">{t('T10002')}</h2>
      <div className="section_heading type_main">
        <button
          type="button"
          className="btn_monthly body_medium_b skeleton"
          style={{width: '160px', height: '20px'}}
        />
        <h3
          className="section_heading_title skeleton"
          style={{width: '200px', height: '28px'}}
        />
        <span
          className="section_heading_caption skeleton"
          style={{width: '160px', height: '20px'}}
        />
      </div>
      <div className="section_container skeleton">
        <div className="section_container_header type_border">
          <div className="device_info_item">
            <h4 className="device_info_key body_medium_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('T10078')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('T10079')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('T10080')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('T10081')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="section_container section_container_small skeleton">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10037')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </span>
        </div>
      </div>
      <div className="section_container section_container_small skeleton">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10072')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  const today = new Date();

  return (
    <section className="section">
      <h2 className="blind">{t('T10002')}</h2>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10070')}
        </h3>
        <span className="section_heading_caption">
          {t('T10077', {date: CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')})}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div className="device_info_item">
            <h4 className="device_info_key body_medium_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('T10078')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('T10079')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('T10080')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('T10081')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10037')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </span>
        </div>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10072')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}
